import * as Sentry from '@sentry/react';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LOGGING_ENABLED =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEBUG_LOGS === 'true';
const STREAM_TO_SENTRY =
  import.meta.env.VITE_STREAM_LOGS_TO_SENTRY === 'true';

const formatMessage = (message?: unknown) =>
  typeof message === 'string' ? message : JSON.stringify(message);

const sendToSentry = (
  level: LogLevel,
  message?: unknown,
  optionalParams?: unknown[]
) => {
  if (!STREAM_TO_SENTRY || (level !== 'warn' && level !== 'error')) {
    return;
  }

  Sentry.withScope(scope => {
    if (optionalParams && optionalParams.length > 0) {
      scope.setContext('log_context', {
        payload: optionalParams.slice(0, 5),
      });
    }
    scope.setLevel(level === 'warn' ? 'warning' : 'error');
    scope.setTag('beta_log', 'true');
    scope.setExtra('log_source', 'frontend');
    scope.setExtra('debug_enabled', LOGGING_ENABLED);
    Sentry.captureMessage(formatMessage(message));
  });
};

const log = (
  level: LogLevel,
  message?: unknown,
  ...optionalParams: unknown[]
) => {
  if (LOGGING_ENABLED) {
    const consoleMethod =
      level === 'debug'
        ? console.debug
        : level === 'info'
        ? console.info
        : level === 'warn'
        ? console.warn
        : console.error;

    consoleMethod(`[FAB] ${formatMessage(message)}`, ...optionalParams);
  }

  sendToSentry(level, message, optionalParams);
};

export const logger = {
  debug: (message?: unknown, ...optionalParams: unknown[]) =>
    log('debug', message, ...optionalParams),
  info: (message?: unknown, ...optionalParams: unknown[]) =>
    log('info', message, ...optionalParams),
  warn: (message?: unknown, ...optionalParams: unknown[]) =>
    log('warn', message, ...optionalParams),
  error: (message?: unknown, ...optionalParams: unknown[]) =>
    log('error', message, ...optionalParams),
};
